/**
 * Shared defect form body — the screen-03 ("Mangel melden") layout, extracted so the
 * CREATE (`DefectReportDialog`) and EDIT (`DefectBaseDialog`) surfaces render the
 * **identical** severity-cards + title + description + photo-evidence block (TES-1722).
 *
 * Visual reference = CREATE / screen-03 (TES-955): 4 `SeveritySelectorCards`, a
 * `PhotoDropzone` and a large `PhotoEvidenceGrid` (4-col, aspect-square, click → lightbox).
 * This is purely presentational — all state and persistence stay in the parent dialogs, so
 * there is NO behavioural change to payload/validation. Severity SoT stays `@/config/severity`.
 *
 * Mode-specific fields (Status / Zuweisung / Ersteller / Cancel-Defect in EDIT) are passed in
 * as `children` / slots and rendered with the same section spacing for a consistent layout.
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ClipboardList, Image as ImageIcon } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FieldError, inputErrorClass } from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DetailSection } from "@/components/ui/detail-layout";
import { PhotoDropzone } from "@/components/ui/photo-dropzone";
import { SeveritySelectorCards } from "@/components/defects/SeveritySelectorCards";
import { PhotoEvidenceGrid } from "@/components/defects/PhotoEvidenceGrid";
import type { DefectSeverity } from "@/types";

export interface DefectFormBodyProps {
  /** ---------- Severity (4-card selector — identical in create + edit) ---------- */
  severity: DefectSeverity;
  onSeverityChange: (value: DefectSeverity) => void;

  /** ---------- Title (required) ---------- */
  title: string;
  onTitleChange: (value: string) => void;
  /**
   * TES-1733 (D2): Inline-Feld-Validierung — rahmt das Titel-Feld rot ein und rendert
   * einen `<FieldError>` darunter (Muster aus TES-1724/#650), nicht nur Toast.
   */
  titleError?: string | null;

  /** ---------- Description ---------- */
  description: string;
  onDescriptionChange: (value: string) => void;

  /**
   * ---------- Category (TES-1693, optional) ----------
   * Self-administrable defect category (`defect_category`). The selector only renders when
   * `onCategoryChange` is provided, so surfaces without category wiring stay unchanged.
   */
  categoryId?: string | null;
  onCategoryChange?: (value: string | null) => void;
  categoryOptions?: { id: string; name: string }[];

  /** ---------- Photo evidence ---------- */
  /** Already-persisted image paths/URLs (edit mode); rendered first, with lightbox. */
  existingImages?: string[];
  /** Remove a persisted image by its index within `existingImages`. */
  onRemoveExistingImage?: (index: number) => void;
  /** Base64 preview strings for newly selected files. */
  imagePreviews: string[];
  /** Remove a newly selected file by its index within `imagePreviews`. */
  onRemoveNewImage: (index: number) => void;
  /** Append the chosen files (capture-only; parent owns upload). */
  onAddFiles: (files: File[]) => void;
  /** Max image count — hides the dropzone once reached. */
  maxImages: number;

  /** Disable inputs while saving. */
  disabled?: boolean;
  /** Unique id prefix to avoid label/input collisions when dialogs mount together. */
  inputIdPrefix: string;

  /**
   * Mode-specific extra sections rendered after the photo block with the same
   * section spacing (e.g. assignee, status, created-by, SAP-sync toggle).
   */
  children?: ReactNode;

  /**
   * TES-2042: layout variant. `grid` (default) keeps the two-column desktop raster
   * (form left, meta card right) — the CREATE dialog's shape. `stacked` renders the
   * same section cards in ONE column (form → photos → meta), for callers that place
   * the body inside an outer detail grid (DefectBaseDialog's edit mode, whose right
   * rail is the shared DefectDetailAside).
   */
  variant?: "grid" | "stacked";
}

/**
 * Renders the unified create/edit defect form body. The combined image list
 * (`existingImages` + `imagePreviews`) goes through a single `PhotoEvidenceGrid`
 * so every tile is large (4-col, aspect-square) and clickable into the shared
 * lightbox, while the per-tile remove control is routed back to the correct
 * source array by index.
 */
// fallow-ignore-next-line complexity
export function DefectFormBody({
  severity,
  onSeverityChange,
  title,
  onTitleChange,
  titleError = null,
  description,
  onDescriptionChange,
  categoryId = null,
  onCategoryChange,
  categoryOptions = [],
  existingImages = [],
  onRemoveExistingImage,
  imagePreviews,
  onRemoveNewImage,
  onAddFiles,
  maxImages,
  disabled = false,
  inputIdPrefix,
  children,
  variant = "grid",
}: DefectFormBodyProps) {
  const { t } = useTranslation();

  const allImages = [...existingImages, ...imagePreviews];
  const totalImages = allImages.length;
  const existingCount = existingImages.length;

  /** Route the grid's flat remove index back to the existing- or new-image array. */
  const handleRemove = (index: number) => {
    if (index < existingCount) {
      onRemoveExistingImage?.(index);
    } else {
      onRemoveNewImage(index - existingCount);
    }
  };

  return (
    <div className="py-4">
      {/* TES-1998: Edit/Create sprechen dieselbe visuelle Familie wie die Detail-Ansicht —
          Sektions-Cards (@plasser/ui DetailSection); `grid` als 2-Spalten-Raster auf Desktop
          (links Kerndaten + Fotos, rechts Zuordnung/Planung), `stacked` (TES-2042) einspaltig
          fuer den Einsatz in einem aeusseren DetailGrid. Mobil immer gestapelt. */}
      <div className={variant === "grid" ? "grid grid-cols-1 gap-4 lg:grid-cols-5" : undefined}>
        <div
          className={variant === "grid" ? "min-w-0 space-y-4 lg:col-span-3" : "min-w-0 space-y-4"}
        >
          <DetailSection className="space-y-6">
            {/* Severity — 4-card selector (real defect_severity enum), identical in create + edit */}
            <section className="space-y-3">
              <Label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {t("defects.dialog.selectSeverity")}
              </Label>
              <SeveritySelectorCards
                value={severity}
                onChange={onSeverityChange}
                showCaptions
                disabled={disabled}
              />
            </section>

            {/* Title — Pflichtfeld mit Inline-Fehler (TES-1733 D2, Muster TES-1724/#650) */}
            <section className="space-y-2">
              <Label htmlFor={`${inputIdPrefix}-title`}>{t("defects.dialog.titleLabel")} *</Label>
              <Input
                id={`${inputIdPrefix}-title`}
                value={title}
                onChange={(e) => onTitleChange(e.target.value)}
                placeholder={t("defects.dialog.titlePlaceholder")}
                disabled={disabled}
                className={inputErrorClass(!!titleError)}
                aria-invalid={!!titleError}
                aria-describedby={titleError ? `${inputIdPrefix}-title-error` : undefined}
              />
              <FieldError
                id={`${inputIdPrefix}-title-error`}
                data-testid={`${inputIdPrefix}-title-error`}
              >
                {titleError}
              </FieldError>
            </section>

            {/* Detailed description */}
            <section className="space-y-2">
              <Label htmlFor={`${inputIdPrefix}-description`}>
                {t("defects.dialog.descriptionDetailLabel")}
              </Label>
              <Textarea
                id={`${inputIdPrefix}-description`}
                value={description}
                onChange={(e) => onDescriptionChange(e.target.value)}
                placeholder={t("defects.dialog.descriptionDetailPlaceholder")}
                className="min-h-[100px]"
                disabled={disabled}
              />
            </section>

            {/* Category — optional self-administrable defect_category (TES-1693) */}
            {onCategoryChange && (
              <section className="space-y-2">
                <Label htmlFor={`${inputIdPrefix}-category`}>
                  {t("defects.category.fieldLabel")}
                </Label>
                <Select
                  value={categoryId ?? "__none__"}
                  onValueChange={(v) => onCategoryChange(v === "__none__" ? null : v)}
                  disabled={disabled}
                >
                  <SelectTrigger
                    id={`${inputIdPrefix}-category`}
                    data-testid="defect-category-select"
                  >
                    <SelectValue placeholder={t("defects.category.selectPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("defects.category.none")}</SelectItem>
                    {categoryOptions.map((c) => (
                      <SelectItem
                        key={c.id}
                        value={c.id}
                        data-testid={`defect-category-option-${c.id}`}
                      >
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </section>
            )}
          </DetailSection>

          {/* Photo evidence — dropzone + large lightbox-enabled grid (visuell abgestuft) */}
          <DetailSection
            icon={<ImageIcon className="h-5 w-5 text-primary" aria-hidden="true" />}
            title={t("defects.dialog.photoSectionLabel")}
          >
            <div className="space-y-3">
              {totalImages < maxImages && (
                <PhotoDropzone
                  onFiles={onAddFiles}
                  disabled={disabled}
                  label={t("defects.dialog.dropzoneLabel")}
                  hint={t("defects.dialog.dropzoneHint")}
                />
              )}
              {totalImages > 0 && (
                <PhotoEvidenceGrid
                  images={allImages}
                  columns={4}
                  clickable
                  onRemove={handleRemove}
                />
              )}
            </div>
          </DetailSection>
        </div>

        {/* Mode-specific meta fields (assignee, status, created-by, …) — the right rail of
            the detail family (grid) bzw. die dritte Sektions-Card (stacked, TES-2042).
            Rendered only when the parent passes sections. */}
        {children && (
          <div
            className={
              variant === "grid" ? "min-w-0 space-y-4 lg:col-span-2" : "min-w-0 space-y-4 mt-4"
            }
          >
            <DetailSection
              icon={<ClipboardList className="h-5 w-5 text-primary" aria-hidden="true" />}
              title={t("defects.dialog.metaSectionLabel")}
            >
              <div className="space-y-6">{children}</div>
            </DetailSection>
          </div>
        )}
      </div>
    </div>
  );
}
